/**
 * Desktop auto-update bridge — wraps `@tauri-apps/plugin-updater` +
 * `@tauri-apps/plugin-process` (2026-05-27).
 *
 * The desktop shell loads the REMOTE SWA, so frontend + backend changes reach
 * users with no reinstall; only NATIVE changes (IPC commands, keychain, the MCP
 * stdio proxy) ship in a new binary. This module lets the app pull those itself
 * instead of the user re-downloading an installer from the website.
 *
 * Flow: `checkForUpdate()` hits the manifest baked into tauri.conf.json
 * (https://aletheiapkm.com/downloads/latest.json), compares versions, and returns
 * a handle if a newer one is published. `installUpdateAndRelaunch()` downloads it,
 * verifies it against the embedded pubkey, installs, and relaunches into the new
 * version.
 *
 * Every call lazy-imports the Tauri JS (so the web bundle never eager-loads it),
 * is gated on `isDesktopApp()`, and swallows errors — the web build and OLDER
 * desktop shells (shipped before the updater plugin existed, e.g. v0.1.12) just
 * no-op. Mirrors the lazy-import discipline in `desktopIpc.ts`.
 */

import { isDesktopApp } from './isDesktopApp';

export interface AvailableUpdate {
  /** The version offered by the manifest, e.g. "0.1.14". */
  version: string;
  /** The version currently running, e.g. "0.1.13". */
  currentVersion: string;
  /** Release notes from the manifest, if any. */
  notes: string | null;
  /** Opaque `@tauri-apps/plugin-updater` Update instance — reused by install. */
  handle: unknown;
}

/** Structural shape of the plugin's `Update` object (avoids a top-level import). */
interface TauriUpdate {
  version: string;
  currentVersion: string;
  body?: string | null;
  downloadAndInstall: (onEvent?: (event: unknown) => void) => Promise<void>;
}

/** Download-progress event shape emitted by `downloadAndInstall`. */
interface DownloadEvent {
  event?: 'Started' | 'Progress' | 'Finished';
  data?: { contentLength?: number; chunkLength?: number };
}

/**
 * Check the update endpoint. Returns the available update, or `null` when the
 * app is up to date, not running in the desktop shell, or the updater is
 * unavailable (older shell / web build / offline). Never throws.
 */
export async function checkForUpdate(): Promise<AvailableUpdate | null> {
  if (!isDesktopApp()) return null;
  try {
    const mod = await import('@tauri-apps/plugin-updater');
    const update = (await mod.check()) as TauriUpdate | null;
    if (!update) return null;
    return {
      version: update.version,
      currentVersion: update.currentVersion,
      notes: update.body ?? null,
      handle: update,
    };
  } catch (err) {
    // Older desktop shells lack the updater command; web has no Tauri at all.
    // eslint-disable-next-line no-console
    console.warn('[updater] check failed (non-fatal)', err);
    return null;
  }
}

/**
 * Download + install the given update, then relaunch into the new version.
 * `onProgress` receives a 0..1 fraction as bytes arrive (best-effort). Throws if
 * the download/verify/install fails — the app does NOT relaunch on failure, so
 * callers should surface the error and leave the current version running.
 */
export async function installUpdateAndRelaunch(
  update: AvailableUpdate,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  const handle = update.handle as TauriUpdate;
  let downloaded = 0;
  let total = 0;
  await handle.downloadAndInstall((event: unknown) => {
    if (!onProgress) return;
    const e = event as DownloadEvent;
    if (e.event === 'Started') {
      total = e.data?.contentLength ?? 0;
      onProgress(0);
    } else if (e.event === 'Progress') {
      downloaded += e.data?.chunkLength ?? 0;
      if (total > 0) onProgress(Math.min(1, downloaded / total));
    } else if (e.event === 'Finished') {
      onProgress(1);
    }
  });
  const proc = await import('@tauri-apps/plugin-process');
  await proc.relaunch();
}

/** Current app version (e.g. "0.1.13"), or `null` on web / error. */
export async function getAppVersion(): Promise<string | null> {
  if (!isDesktopApp()) return null;
  try {
    const mod = await import('@tauri-apps/api/app');
    return await mod.getVersion();
  } catch {
    return null;
  }
}
